import { DatePicker } from '@components/common/DatePicker';
import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import { Download, RefreshCw } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  RevParReportRow,
  fetchRevParReport,
} from '../../../../services/accommodationService';
import { apiError } from '../../../../util/notify';
import { RevParTrendChart } from '../RevParTrendChart';
import {
  currentMonthRange,
  exportToCsv,
  fmtAmount,
  fmtDate,
} from './reportUtils';

const { from: initFrom, to: initTo } = currentMonthRange();

function buildDays(from: string, to: string): string[] {
  const days: string[] = [];
  const end = new Date(to);
  for (let d = new Date(from); d <= end; d.setDate(d.getDate() + 1)) {
    days.push(d.toISOString().split('T')[0]);
  }
  return days;
}

export const RevParReportPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const currency = property?.currency ?? '';

  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);
  const [rows, setRows] = useState<RevParReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!property) {
      apiError('Please select a property before running the RevPAR report.');
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchRevParReport(property.id, dateFrom, dateTo);
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [property, dateFrom, dateTo]);

  const normalizedRows = useMemo(() => {
    const totalRooms = rows[0]?.total_rooms_available ?? 0;
    const byDate = new Map(rows.map((row) => [row.report_date, row]));

    return buildDays(dateFrom, dateTo).map((day) => {
      const row = byDate.get(day);
      return (
        row ?? {
          property_id: property?.id ?? '',
          outlet_id: property?.outlet_id ?? '',
          property_name: property?.name ?? '',
          currency,
          report_date: day,
          total_rooms_available: totalRooms,
          rooms_occupied: 0,
          total_room_revenue: 0,
          occupancy_pct: 0,
          revpar: 0,
        }
      );
    });
  }, [rows, dateFrom, dateTo, property, currency]);

  const trendData = useMemo(
    () =>
      normalizedRows.map((row) => ({
        label: fmtDate(row.report_date).slice(0, 6),
        revpar: row.revpar,
      })),
    [normalizedRows],
  );

  const totalRevenue = normalizedRows.reduce(
    (sum, row) => sum + row.total_room_revenue,
    0,
  );
  const totalAvailableRoomNights = normalizedRows.reduce(
    (sum, row) => sum + row.total_rooms_available,
    0,
  );
  const totalOccupiedRoomNights = normalizedRows.reduce(
    (sum, row) => sum + row.rooms_occupied,
    0,
  );
  const avgRevPar =
    totalAvailableRoomNights > 0 ? totalRevenue / totalAvailableRoomNights : 0;
  const avgOccupancy =
    totalAvailableRoomNights > 0
      ? (totalOccupiedRoomNights / totalAvailableRoomNights) * 100
      : 0;
  const bestDay = normalizedRows.reduce<RevParReportRow | null>(
    (best, row) => (best === null || row.revpar > best.revpar ? row : best),
    null,
  );

  const handleCsv = () => {
    exportToCsv(
      `revpar-report-${dateFrom}-${dateTo}`,
      normalizedRows.map((row) => ({
        Date: row.report_date,
        'Available Rooms': row.total_rooms_available,
        'Occupied Rooms': row.rooms_occupied,
        [`Revenue (${currency})`]: fmtAmount(row.total_room_revenue),
        'Occupancy %': row.occupancy_pct,
        RevPAR: fmtAmount(row.revpar),
      })),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <DatePicker
          mode="single"
          id="revpar-date-from"
          label="From date"
          value={dateFrom}
          onChange={setDateFrom}
        />
        <DatePicker
          mode="single"
          id="revpar-date-to"
          label="To date"
          value={dateTo}
          onChange={setDateTo}
        />
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Loading…' : 'Run report'}
        </button>
        {loaded && (
          <button
            type="button"
            onClick={handleCsv}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {loaded && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: 'Avg. RevPAR',
                value: `${currency} ${fmtAmount(avgRevPar)}`,
                sub: `${normalizedRows.length} days`,
              },
              {
                label: 'Room Revenue',
                value: `${currency} ${fmtAmount(totalRevenue)}`,
                sub: 'allocated daily',
              },
              {
                label: 'Avg. Occupancy',
                value: `${avgOccupancy.toFixed(1)}%`,
                sub: `${totalOccupiedRoomNights}/${totalAvailableRoomNights} room-nights`,
              },
              {
                label: 'Best Day',
                value: bestDay
                  ? `${currency} ${fmtAmount(bestDay.revpar)}`
                  : `${currency} 0`,
                sub: bestDay ? fmtDate(bestDay.report_date) : 'No data',
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-2xl border border-gray-200 bg-white p-4"
              >
                <p className="text-xs text-gray-500">{card.label}</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {card.value}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">{card.sub}</p>
              </div>
            ))}
          </div>

          <RevParTrendChart
            title="RevPAR Trend"
            data={trendData}
            isLoading={isLoading}
          />

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                RevPAR detail - {fmtDate(dateFrom)} to {fmtDate(dateTo)}
              </h4>
            </div>
            {normalizedRows.length === 0 ? (
              <div className="flex h-28 items-center justify-center text-sm text-gray-400">
                No RevPAR data for this period.
              </div>
            ) : (
              <div className="overflow-auto max-h-[420px]">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      {[
                        'Date',
                        'Available Rooms',
                        'Occupied Rooms',
                        `Revenue (${currency})`,
                        'Occupancy %',
                        'RevPAR',
                      ].map((header) => (
                        <th
                          key={header}
                          className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {normalizedRows.map((row) => (
                      <tr key={row.report_date} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2.5 font-medium text-gray-900">
                          {fmtDate(row.report_date)}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {row.total_rooms_available}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {row.rooms_occupied}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {fmtAmount(row.total_room_revenue)}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {row.occupancy_pct}%
                        </td>
                        <td className="px-4 py-2.5 font-semibold text-gray-900">
                          {currency} {fmtAmount(row.revpar)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to load data.
        </div>
      )}
    </div>
  );
};
